import type { PlatformChildRow, PlatformChildSession } from "../../../shared/api/platform";

/**
 * The children screen's decisions, out of the component.
 *
 * 🔴 THE REPO HAS NO DOM RENDERER (OD-60), so a branch made inside a `.tsx` is a
 * branch no test can reach — and the branches here decide which children an
 * operator is shown, which ones a search hides, and what a row's activity pill
 * says about a real child. `studentsModel.ts` is the same file one tier down and
 * this one deliberately mirrors its shape rather than inventing a second
 * vocabulary for the same ideas.
 *
 * 🔴 NOTHING HERE COMPUTES A RATE. Every figure that reaches a screen from this
 * file is a count or a pair — OD-39/OD-72 — and the one place a share would be
 * tempting (a child's accuracy) is the one place it would lie loudest: three
 * answered questions is not 33 %.
 */

/* ────────────────────────────────────────────────── one child's activity ── */

/**
 * 🔴 FOUR STATES, NOT THREE, AND THE FOURTH IS THE ONE THIS TIER EXISTS TO SEE.
 *
 * The school tier has `NEVER | RECENT | EARLIER`, read from `Progress.lastPlayed`
 * — what the child's own device last wrote. This reads `lastPlayedAt`, which is
 * folded from the EVENT stream, and the platform tier can therefore see a state
 * a school cannot: a child with sittings whose events were never written at all
 * (`recorded: false`, ADR-030). To every count in the product they look like a
 * child who has done nothing, and only one of those two is a finding.
 */
export type ActivityState = "NEVER" | "RECENT" | "EARLIER" | "UNRECORDED";

const WEEK_MS = 7 * 86_400_000;

export function activityState(row: PlatformChildRow, now: Date): ActivityState {
  if (row.lastPlayedAt === null) {
    /*
     * ⚠️ CHECKED BEFORE "NEVER", BECAUSE IT IS A DIFFERENT SENTENCE. A child
     * with unrecorded sittings and no events has sat at the laptop; saying
     * "never played" about them is wrong in the direction that would send a
     * teacher to chase a child who has been playing all term.
     */
    return row.unrecordedSessions > 0 ? "UNRECORDED" : "NEVER";
  }
  const at = new Date(row.lastPlayedAt).getTime();
  // An unparseable date is not evidence that a child played. Never, not recent.
  if (!Number.isFinite(at)) return "NEVER";
  return now.getTime() - at <= WEEK_MS ? "RECENT" : "EARLIER";
}

/* ─────────────────────────────────────────────────────── what is shown ── */

export type ActivityFilter = "ALL" | "RECENT" | "QUIET" | "NEVER" | "UNRECORDED";

export const ACTIVITY_FILTERS: readonly ActivityFilter[] = [
  "ALL",
  "RECENT",
  "QUIET",
  "NEVER",
  "UNRECORDED",
];

export interface ChildFilter {
  /** Free text over name, roll, class and school. Trimmed and case-folded. */
  query?: string;
  /** One school, or every school. */
  schoolId?: string | null;
  activity?: ActivityFilter;
  /**
   * 🔴 THE CHILDREN NO SCHOOL CAN SEE. F1 counts them
   * (`enrolment.unattributedChildren`) and until this screen nothing could name
   * them — `Player.classId` is `SetNull`, so a child between classes is
   * invisible to every scoped query in the product.
   */
  unattributedOnly?: boolean;
}

/**
 * ⚠️ THE PARENT'S NAME IS SEARCHABLE AND THEIR NUMBER IS NOT ON THIS TIER AT
 * ALL. `PlatformChildRow` carries no phone, masked or otherwise — an operator
 * does not ring a family, and every field on that response crosses school
 * boundaries. The school tier keeps the masked number, and searching it there is
 * refused for its own reason (`studentsModel.ts`).
 */
export function matchesQuery(row: PlatformChildRow, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (q === "") return true;
  return [row.name, row.rollNumber, row.className, row.schoolName, row.parentName].some(
    (field) => field !== null && field.toLowerCase().includes(q),
  );
}

export function filterChildren(
  rows: readonly PlatformChildRow[],
  filter: ChildFilter,
  now: Date,
): PlatformChildRow[] {
  const {
    query = "",
    schoolId = null,
    activity = "ALL",
    unattributedOnly = false,
  } = filter;
  return rows.filter((row) => {
    if (unattributedOnly && row.schoolId !== null) return false;
    if (schoolId !== null && row.schoolId !== schoolId) return false;
    if (!matchesQuery(row, query)) return false;
    if (activity === "ALL") return true;
    const state = activityState(row, now);
    if (activity === "RECENT") return state === "RECENT";
    if (activity === "NEVER") return state === "NEVER";
    if (activity === "UNRECORDED") return state === "UNRECORDED";
    /* QUIET is "has played, but not lately" — a different child from one who has
       never started, and a different conversation with their school. */
    return state === "EARLIER";
  });
}

/* ─────────────────────────────────────────────────────── the school list ── */

export interface SchoolOption {
  id: string;
  name: string;
  children: number;
}

/**
 * The schools to offer in the filter, with how many children each has on screen.
 *
 * 🔴 BUILT FROM THE ROWS RATHER THAN FETCHED. A second request to
 * `/superadmin/schools` would be a second answer to *"which schools exist"* that
 * could disagree with the list underneath it — and a school with no children at
 * all is not a useful option on a screen about children.
 *
 * ⚠️ CHILDREN WITH NO SCHOOL ARE NOT AN OPTION HERE. They get their own toggle
 * (`unattributedOnly`), because "no school" is a finding rather than a place.
 */
export function schoolOptions(rows: readonly PlatformChildRow[]): SchoolOption[] {
  const by = new Map<string, SchoolOption>();
  for (const row of rows) {
    if (row.schoolId === null) continue;
    const found = by.get(row.schoolId);
    if (found) {
      found.children += 1;
      continue;
    }
    by.set(row.schoolId, {
      id: row.schoolId,
      name: row.schoolName ?? row.schoolId,
      children: 1,
    });
  }
  return [...by.values()].sort((a, b) => a.name.localeCompare(b.name));
}

/* ───────────────────────────────────────────────────────────── the totals ── */

export interface ChildTotals {
  children: number;
  played: number;
  recent: number;
  never: number;
  unattributed: number;
  unrecorded: number;
  rounds: number;
  sessions: number;
}

/**
 * The tiles above the table.
 *
 * 🔴 EVERY ONE IS A COUNT WITH `children` AS ITS DENOMINATOR, and the screen
 * prints the pair. *"18 of 412 played this week"* is a fact an operator can act
 * on; *"4 % engagement"* is a grade whose meaning depends on what it was divided
 * by — the rule `health-score.ts` states on the server and `copy.ts` restates
 * for every sentence on this tier.
 */
export function summariseChildren(
  rows: readonly PlatformChildRow[],
  now: Date,
): ChildTotals {
  const totals: ChildTotals = {
    children: rows.length,
    played: 0,
    recent: 0,
    never: 0,
    unattributed: 0,
    unrecorded: 0,
    rounds: 0,
    sessions: 0,
  };
  for (const row of rows) {
    if (row.schoolId === null) totals.unattributed += 1;
    if (row.unrecordedSessions > 0) totals.unrecorded += 1;
    totals.rounds += row.rounds;
    totals.sessions += row.sessions;
    const state = activityState(row, now);
    if (state === "RECENT") {
      totals.recent += 1;
      totals.played += 1;
    } else if (state === "EARLIER") {
      totals.played += 1;
    } else if (state === "NEVER") {
      totals.never += 1;
    }
  }
  return totals;
}

/* ───────────────────────────────────────────────────────────── one child ── */

/**
 * How long a child has actually been playing, over the sittings on screen.
 *
 * 🔴 `null` WHEN NOTHING CAN BE MEASURED, NEVER `0`. A sitting nothing closed
 * has no duration (OD-37 — the device closes sessions, and a laptop that lost
 * power never does), so a child whose every session is open contributes no
 * minutes at all. Reporting zero would say they sat down and left.
 *
 * ⚠️ IT IS "OVER THE SITTINGS ON SCREEN", NOT "EVER", and the screen says so.
 * The detail read is capped at sixty sessions; a total labelled as a lifetime
 * would be wrong for exactly the children who play most.
 */
export function minutesPlayed(
  sessions: readonly PlatformChildSession[],
): { minutes: number; ofSessions: number; measured: number } | null {
  const measured = sessions.filter((s) => s.minutes !== null);
  if (measured.length === 0) return null;
  return {
    minutes: measured.reduce((sum, s) => sum + (s.minutes ?? 0), 0),
    ofSessions: sessions.length,
    measured: measured.length,
  };
}

/**
 * A child's answers as a pair, and the verdict about whether it means anything.
 *
 * 🔴 BELOW TEN ANSWERS THERE IS NO ACCURACY WORTH READING, AND THIS RETURNS
 * `null` RATHER THAN A SMALL SAMPLE. `attention.ts` on the teacher tier sets the
 * same floor for the same reason, in its own words: *"one attempt is 0 % or
 * 100 %… which is every child on their first day."* An operator looking at four
 * hundred children will sort by this column if it exists, and a column that
 * ranks first-day children at the bottom is a column that generates phone calls
 * about nothing.
 */
export const MIN_ANSWERS_FOR_ACCURACY = 10;

export function accuracyPair(
  row: PlatformChildRow,
): { correct: number; total: number } | null {
  const total = row.correct + row.wrong;
  if (total < MIN_ANSWERS_FOR_ACCURACY) return null;
  return { correct: row.correct, total };
}

/* ───────────────────────────────────────────────────────────── the export ── */

/**
 * The rows as a file.
 *
 * 🔴 IT CARRIES NO PARENT NAME, AND THAT IS THE ONE FIELD DROPPED ON PURPOSE.
 * A CSV is the response that leaves the building — `auditCsv` states the rule —
 * and a spreadsheet of children's names beside their parents' names on an
 * operator's laptop is a bigger artefact than a table on a screen. The child's
 * own name stays, because a file identifying children by cuid is a file nobody
 * can check against reality.
 *
 * ⚠️ FORMULA INJECTION IS ESCAPED HERE TOO. A school's name reaches this file
 * and is chosen by a customer; `=cmd|…` in a cell executes when the file is
 * opened. Same rule as `auditRowsToCsv` and `coverageToCsv` on the server.
 */
export function childrenToCsv(rows: readonly PlatformChildRow[]): string {
  const header = [
    "Child",
    "Roll",
    "School",
    "Class",
    "Sessions",
    "Rounds",
    "Right",
    "Wrong",
    "Games",
    "First played",
    "Last played",
    "Unrecorded sittings",
  ];
  const lines = [header.map(cell).join(",")];
  for (const row of rows) {
    lines.push(
      [
        row.name,
        row.rollNumber ?? "",
        row.schoolName ?? "",
        row.className ?? "",
        String(row.sessions),
        String(row.rounds),
        String(row.correct),
        String(row.wrong),
        String(row.gamesPlayed),
        row.firstPlayedAt ?? "",
        row.lastPlayedAt ?? "",
        String(row.unrecordedSessions),
      ]
        .map(cell)
        .join(","),
    );
  }
  /* `\r\n`, matching every other CSV this product emits. Two dialects in one
     product is how a school's import of our own export fails. */
  return lines.join("\r\n");
}

function cell(value: string): string {
  /* A leading `=`, `+`, `-`, `@`, tab or carriage return is a formula to a
     spreadsheet. Prefixed with an apostrophe, which Excel and Sheets both read
     as "this is text".

     ⚠️ THE CHARACTER SET IS COPIED, NOT CHOSEN. `backend/common/csv/csv-cell.ts`
     is the canonical one and names its copies; this file was a fourth, and it
     was the only one missing `\t` and `\r` — the two that look like whitespace
     and are treated as a formula lead-in anyway. Change one, change all four. */
  const guarded = /^[=+\-@\t\r]/.test(value) ? `'${value}` : value;
  return /[",\r\n]/.test(guarded) ? `"${guarded.replace(/"/g, '""')}"` : guarded;
}
